import React from "react";
import { Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../constants";
import registrationPageImg from "../../assets/images/screenshots/registration-page.webp";
import invalidRegistrationPageImg from "../../assets/images/screenshots/invalid-registration-page.webp";
import otpPageImg from "../../assets/images/screenshots/OTP-page.webp";
import twoFaPageImg from "../../assets/images/screenshots/2FA-page.webp";
import pre2StepImg from "../../assets/images/screenshots/pre-2-step.webp";
import {
  PageContainer,
  ContentBox,
  Title,
  SectionTitle,
  Paragraph,
  BulletList,
  BulletItem,
  ConsentBox,
  ConsentText,
  ScreenshotRow,
  ScreenshotImage,
  InlineLink,
  Divider,
  Note,
} from "./SmsPolicyPage.styled";

const SmsPolicyPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer>
      <Container maxWidth="md">
        <ContentBox>
          <Title>{t("smsPolicy.title")}</Title>

          {/* ── Intro ── */}
          <Paragraph>{t("smsPolicy.intro.p1")}</Paragraph>
          <Paragraph>{t("smsPolicy.intro.p2")}</Paragraph>
          <Paragraph>{t("smsPolicy.intro.p3")}</Paragraph>

          {/* ── Section 1 ── */}
          <SectionTitle>
            {t("smsPolicy.sections.registrationFlow.title")}
          </SectionTitle>
          <Paragraph>{t("smsPolicy.sections.registrationFlow.p1")}</Paragraph>
          <Paragraph>{t("smsPolicy.sections.registrationFlow.p2")}</Paragraph>
          <ScreenshotRow>
            <ScreenshotImage
              src={registrationPageImg}
              alt={t("smsPolicy.sections.registrationFlow.imgAlt")}
            />
          </ScreenshotRow>

          {/* ── Section 2 ── */}
          <SectionTitle>
            {t("smsPolicy.sections.consent.title")}
          </SectionTitle>
          <Paragraph>{t("smsPolicy.sections.consent.p1")}</Paragraph>
          <Paragraph>{t("smsPolicy.sections.consent.p2")}</Paragraph>
          <BulletList>
            <BulletItem>{t("smsPolicy.sections.consent.bullet1")}</BulletItem>
            <BulletItem>{t("smsPolicy.sections.consent.bullet2")}</BulletItem>
          </BulletList>
          <Paragraph>{t("smsPolicy.sections.consent.p3")}</Paragraph>
          <ScreenshotRow>
            <ScreenshotImage
              src={invalidRegistrationPageImg}
              alt={t("smsPolicy.sections.consent.imgAlt")}
            />
          </ScreenshotRow>

          {/* ── Section 3 ── */}
          <SectionTitle>
            {t("smsPolicy.sections.smsAuthFlow.title")}
          </SectionTitle>
          <Paragraph>{t("smsPolicy.sections.smsAuthFlow.p1")}</Paragraph>
          <Paragraph>{t("smsPolicy.sections.smsAuthFlow.p2")}</Paragraph>
          <Paragraph>{t("smsPolicy.sections.smsAuthFlow.p3")}</Paragraph>
          <ScreenshotRow>
            <ScreenshotImage
              src={pre2StepImg}
              alt={t("smsPolicy.sections.smsAuthFlow.img1Alt")}
            />
            <ScreenshotImage
              src={twoFaPageImg}
              alt={t("smsPolicy.sections.smsAuthFlow.img2Alt")}
            />
          </ScreenshotRow>

          {/* ── Section 4 ── */}
          <SectionTitle>
            {t("smsPolicy.sections.smsVerification.title")}
          </SectionTitle>
          <Paragraph>{t("smsPolicy.sections.smsVerification.p1")}</Paragraph>
          <Paragraph>{t("smsPolicy.sections.smsVerification.p2")}</Paragraph>
          <ScreenshotRow>
            <ScreenshotImage
              src={otpPageImg}
              alt={t("smsPolicy.sections.smsVerification.imgAlt")}
            />
          </ScreenshotRow>

          {/* ── Section 5 ── */}
          <SectionTitle>
            {t("smsPolicy.sections.messages.title")}
          </SectionTitle>
          <Paragraph>{t("smsPolicy.sections.messages.p1")}</Paragraph>
          <BulletList>
            <BulletItem>{t("smsPolicy.sections.messages.bullet1")}</BulletItem>
            <BulletItem>{t("smsPolicy.sections.messages.bullet2")}</BulletItem>
            <BulletItem>{t("smsPolicy.sections.messages.bullet3")}</BulletItem>
          </BulletList>
          <Paragraph>{t("smsPolicy.sections.messages.p2")}</Paragraph>
          <Paragraph>{t("smsPolicy.sections.messages.p3")}</Paragraph>

          {/* ── Section 6 ── */}
          <SectionTitle>
            {t("smsPolicy.sections.consentLanguage.title")}
          </SectionTitle>
          <ConsentBox>
            <ConsentText>
              {t("smsPolicy.sections.consentLanguage.text")}
            </ConsentText>
          </ConsentBox>

          {/* ── Section 7 ── */}
          <SectionTitle>
            {t("smsPolicy.sections.disclosures.title")}
          </SectionTitle>
          <BulletList>
            <BulletItem>
              {t("smsPolicy.sections.disclosures.bullet1")}
            </BulletItem>
            <BulletItem>
              {t("smsPolicy.sections.disclosures.bullet2")}
            </BulletItem>
            <BulletItem>
              {t("smsPolicy.sections.disclosures.bullet3")}
            </BulletItem>
            <BulletItem>
              {t("smsPolicy.sections.disclosures.bullet4")}
            </BulletItem>
          </BulletList>
          <Paragraph>
            {t("smsPolicy.sections.disclosures.moreInfo")}{" "}
            <InlineLink
              href={ROUTES.TERMS}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("smsPolicy.sections.disclosures.terms")}
            </InlineLink>{" "}
            {t("smsPolicy.sections.disclosures.and")}{" "}
            <InlineLink
              href={ROUTES.PRIVACY}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("smsPolicy.sections.disclosures.privacy")}
            </InlineLink>
            .
          </Paragraph>

          {/* ── Footer note ── */}
          <Divider />
          <Note>
            {t("smsPolicy.note.p1")}
            <br />
            <br />
            {t("smsPolicy.note.p2")}
          </Note>
        </ContentBox>
      </Container>
    </PageContainer>
  );
};

export default SmsPolicyPage;
